import React from 'react';
import { theme } from '../../theme';

// Top-of-page stat card. Small uppercase label + big colored number, with
// an optional accent bar on the left so a row of these reads as a dashboard.
//
// Props:
//   label    short uppercase label
//   value    main number/string (already formatted, e.g. ₹12,345)
//   tone     'primary' | 'success' | 'warning' | 'danger' | 'info' | 'navy'
//   sub      optional smaller second line under the value
//   icon     optional ReactNode rendered top-right
//   onClick  optional — makes the whole card clickable
const TONE = {
  primary: theme.primary,
  success: theme.success,
  warning: theme.warning,
  danger:  theme.danger,
  info:    theme.info,
  navy:    theme.navy,
};

export default function StatCard({
  label,
  title,             // alias for `label` (some pages prefer the "title" naming)
  value,
  tone = 'navy',
  accent,            // alias: a hex/CSS color OR a tone key — overrides tone when set
  sub,
  icon,
  onClick,
}) {
  // Accept either a tone key or a literal color string for `accent`.
  const accentColor = typeof accent === 'string' && accent.startsWith('#') ? accent
    : (TONE[accent] || null);
  const color = accentColor || TONE[tone] || theme.navy;
  const labelText = label || title || '';
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: theme.surface,
        borderRadius: theme.radiusLg,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
        padding: '16px 18px',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.12s, box-shadow 0.12s',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.boxShadow = theme.shadowMd; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.boxShadow = theme.shadow; } : undefined}
    >
      {/* Left accent bar — gives the row of cards a subtle traffic-light feel */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: color }}/>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{labelText}</div>
          <div style={{ marginTop: '4px', fontSize: '1.5rem', color, fontWeight: '700', lineHeight: '1.15' }}>{value}</div>
          {sub && <div style={{ marginTop: '2px', fontSize: '0.75rem', color: theme.textMuted }}>{sub}</div>}
        </div>
        {icon && <div style={{ color: color + 'cc', flexShrink: 0 }}>{icon}</div>}
      </div>
    </div>
  );
}
